"use client";

import Link from "next/link";
import { formatPrice } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { ListingImage } from "@/components/ui/listing-image";
import { CARD, CARD_SM, type ImagePreset } from "@/lib/images/presets";

interface MiniListingCardProps {
  listingId: string;
  image: string;
  name: string;
  price: number;
  currency?: string | null;
  scheduledPublishAt?: string | null;
  size?: "sm" | "md" | "lg" | "lgFluid";
  interactive?: boolean;
  priceOnly?: boolean;
}

const widthMap = {
  sm: "w-24",
  md: "w-32",
  lg: "w-64",
  lgFluid: "w-full max-w-64",
} as const;

/**
 * Which RUNG each size renders at — a selection from the closed set, never a
 * number of its own.
 *
 * What this replaced: a `thumbWidthMap` of four bare widths plus a derived
 * `thumbHeight` (`w * 4/3`, or `w` when `priceOnly`), i.e. **six distinct
 * (width, height, quality) parameter sets emitted from one file** — six cache
 * entries per photo on the browser AND on the Supabase CDN, none of them shared
 * with any other screen. Structurally that is the same defect as an inline
 * literal: a private preset table hidden in the delivery layer, where nothing
 * can see it drift. @see lib/images/presets.ts for why the COUNT is the thing
 * that costs money.
 *
 * The two rungs below are both already requested elsewhere (`CARD_SM` by the
 * chat listing cards and the share sheet, `CARD` by the feed card, the
 * listing-detail slide and its backdrop), so this file now emits **zero** new
 * cache variants — a mini card of a listing the user already scrolled past
 * paints for 0 B.
 *
 * Coverage, measured against the CSS boxes in `widthMap` at 2x DPR: `sm` (96 px
 * box) and `md` (128 px) need at most 341 / 455 device px for the worst-case
 * source shape (a 4:3 landscape upload, whose cover fit into a 3:4 box is
 * height-driven), so `CARD_SM` covers `sm` outright and is ~15 % short on `md`;
 * `lg` / `lgFluid` (256 px) need up to 911 and `CARD` is ~9 % short. Both
 * shortfalls sit in the same band `CARD` itself already accepts (8 %), and both
 * are an order below the 25-67 % upscales a wrong-aspect rung would cost.
 */
const presetMap: Record<NonNullable<MiniListingCardProps["size"]>, ImagePreset> = {
  sm: CARD_SM,
  md: CARD_SM,
  lg: CARD,
  lgFluid: CARD,
};

export function MiniListingCard({
  listingId,
  image,
  name,
  price,
  currency = null,
  scheduledPublishAt,
  size = "sm",
  interactive = true,
  priceOnly = false,
}: MiniListingCardProps) {
  const isScheduled = scheduledPublishAt && new Date(scheduledPublishAt) > new Date();
  const href = `/listings/${listingId}`;
  const className = cn(
    "block shrink-0 overflow-hidden relative group",
    priceOnly ? "rounded-3xl" : "rounded-2xl",
    widthMap[size],
    priceOnly ? "aspect-square" : "aspect-[3/4]",
    interactive ? "cursor-pointer" : "",
  );

  const content = (
    <>
      <div className="absolute inset-0 bg-muted z-0">
        <ListingImage
          src={image || "/placeholder.svg"}
          alt={name}
          isScheduled={Boolean(isScheduled)}
          skeletonClassName="bg-muted dark:bg-white/5 animate-none"
          className="group-hover:scale-105 transition-all duration-300"
          preset={presetMap[size]}
        />
      </div>
      {isScheduled && (
        <div className="absolute inset-0 bg-black/40 pointer-events-none z-10" />
      )}
      {priceOnly ? (
        <div className="absolute bottom-2 right-2 z-20 pointer-events-none">
          <span className="bg-white text-black rounded-full px-2.5 py-1 text-xs font-medium shadow-sm">
            {formatPrice(price, currency ?? "USD")}
          </span>
        </div>
      ) : (
        <>
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-linear-to-t from-black/80 to-transparent pointer-events-none z-10" />
          <div className="absolute bottom-2 left-2 right-2 flex flex-col justify-end z-20">
            <div className="flex justify-between items-start gap-1.5">
              <p title={name} className={cn("text-white font-medium line-clamp-2 flex-1 min-w-0 leading-tight", (size === "lg" || size === "lgFluid") ? "text-sm" : "text-[10px]")}>{name}</p>
              <span className={cn("bg-background border border-border text-foreground rounded-full font-medium shrink-0 dark:bg-white dark:border-transparent dark:text-black", (size === "lg" || size === "lgFluid") ? "px-3 py-1 text-xs" : "px-2 py-0.5 text-[10px]")}>
                {formatPrice(price, currency ?? "USD")}
              </span>
            </div>
          </div>
        </>
      )}
    </>
  );

  if (!interactive) {
    return <div className={className}>{content}</div>;
  }

  return (
    <Link href={href} className={className}>
      {content}
    </Link>
  );
}

